import React, { useLayoutEffect } from 'react';
import { useNavigation } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import type { AddressType } from '@sg/garnish';
import { BodyText, DisplayText, ProgressBar, TagLabel, theme, useResponsive } from '@sg/garnish';
import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import { OrderStatusCtas } from '../OrderStatusCtas';
import { useOrderStatusDetails } from './hooks/useOrderStatusDetails';
import { useOrderStatusTitle } from './hooks/useOrderStatusTitle';
import { useProgressNodes } from './hooks/useProgressNodes';

// ────────────────────────────────────────────────────────────────────────────────

export const OrderStatusInfo = (props: OrderStatusInfoProps) => {
  const {
    orderId = '',
    orderStatus,
    orderType,
    orderTotal = 0,
    vendorId,
    wantedTime,
    estimatedDeliveryTime,
    locationName,
    locationAddress,
    isTrackable,
    canRateOrder,
    isPast,
    canReorder,
    brandIconSrc,
    brandIconAlt,
    isGroupOrder,
    onReorder,
    onRateOrder,
  } = props;

  const { minWidth, match, currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  // ────────────────────────────────────────────────────────────────

  const shouldShowProgressBar = checkIfShouldShowProgressBar(orderStatus);
  const shouldRenderOrderStatusCTAs = canReorder || canRateOrder;

  const orderStatusTitle = useOrderStatusTitle({
    orderType,
    orderStatus,
    isTrackable,
    isPast,
  });
  const orderStatusDetails = useOrderStatusDetails({
    orderType,
    orderStatus,
    isTrackable,
    isPast,
    wantedTime,
    estimatedDeliveryTime,
    locationName,
    locationAddress,
  });
  const progressNodes = useProgressNodes(orderType, orderStatus);

  // ─── Set screen title ──────────────────────────────────────────

  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({ title: orderStatusTitle });
  }, [navigation, orderStatusTitle]);

  // ─── Styles ────────────────────────────────────────────────────

  const headerStyles = match([styles.infoHeaderXS, styles.infoHeaderSM]);
  const ctasWrapperStyle = match([undefined, styles.ctasWrapperSM]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <View testID="order-status-info">
      <View style={headerStyles}>
        <DisplayText sizeMatch={['32', '40']}>{orderStatusTitle}</DisplayText>

        <View style={styles.orderMetadata}>
          <BodyText size={4} bold={currentBreakpoint.isXS}>
            {formatMessage(messages.orderNumber, { order_id: vendorId })}
          </BodyText>

          {isGroupOrder ? (
            <TagLabel sizeMatch={['10']} bold>
              {formatMessage(messages.groupOrder)}
            </TagLabel>
          ) : null}
        </View>
      </View>

      {orderStatusDetails ? <BodyText>{orderStatusDetails}</BodyText> : null}

      {shouldShowProgressBar ? (
        <View style={styles.progressBar}>
          <ProgressBar
            nodes={progressNodes}
            maxFontSizeMultiplier={1.3}
            iconSrc={brandIconSrc}
            iconAlt={brandIconAlt}
          />
        </View>
      ) : null}

      {minWidth.isSM && shouldRenderOrderStatusCTAs ? (
        <View style={ctasWrapperStyle}>
          <OrderStatusCtas
            orderId={orderId}
            orderTotal={orderTotal}
            canRateOrder={canRateOrder}
            canReorder={canReorder}
            onReorder={onReorder}
            onRateOrder={onRateOrder}
          />
        </View>
      ) : null}
    </View>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  infoHeaderXS: {
    gap: theme.spacing['4'],
  },
  infoHeaderSM: {
    gap: theme.spacing['4'],
    marginBottom: theme.spacing['4'],
    flexDirection: 'column-reverse',
  },
  progressBar: {
    marginTop: theme.spacing['4'],
    paddingTop: theme.spacing['4'],
    minHeight: theme.spacing['10'],
  },
  ctasWrapperSM: {
    paddingTop: theme.spacing['4'],
  },
  orderMetadata: {
    alignItems: 'flex-start',
    gap: theme.spacing['2'],
  },
});

// ─── Utils ──────────────────────────────────────────────────────────────────────

const checkIfShouldShowProgressBar = (orderStatus?: FlattenedOrderStatuses) =>
  orderStatus !== FlattenedOrderStatuses.Completed && orderStatus !== FlattenedOrderStatuses.Failed;

// ─── Types ──────────────────────────────────────────────────────────────────────

export type OrderStatusInfoProps = {
  orderId?: string;
  orderTotal?: number;
  orderType?: AddressType;
  orderStatus?: FlattenedOrderStatuses;
  vendorId?: string;
  wantedTime?: string;
  estimatedDeliveryTime?: string;
  locationName?: string;
  locationAddress?: string;
  isTrackable: boolean;
  canRateOrder: boolean;
  isPast: boolean;
  momoSurveyUrl?: string | null;
  canReorder: boolean;
  brandIconSrc?: string;
  brandIconAlt?: string;
  isGroupOrder?: boolean;
  onReorder?: () => void;
  onRateOrder?: () => void;
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  groupOrder: {
    defaultMessage: 'Group order',
    description: 'Order Status | Info | Group order label',
  },
  orderNumber: {
    defaultMessage: 'Order #{order_id}',
    description: 'Order Status | Info | Order number display',
  },
});
